import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for tracking which page is currently visible in a paginated viewer,
 * such as a PDF viewer or similar scroll-based document viewer.
 *
 * @returns {object} An object containing:
 *   - viewerRef: Ref to the scroll container element.
 *   - pageRefs: Ref array for all individual page elements.
 *   - numPages: Number of pages in the document.
 *   - setNumPages: Function to set the number of pages.
 *   - currentPage: The currently visible page number (1-based index).
 */
export default function usePageTracking() {
    const viewerRef = useRef(null); // Ref to the scrollable viewer container (used as the root for intersection observing)
    const pageRefs = useRef([]); // Ref to hold an array of refs to individual page elements
    const [numPages, setNumPages] = useState(null); // Number of pages in the document
    const [currentPage, setCurrentPage] = useState(1); // Currently visible page (starts at 1)

    // Effect to set up an IntersectionObserver for tracking visible pages
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                // Filter for pages that are currently intersecting (visible in viewport)
                const visible = entries
                    .filter(e => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top); // Sort by vertical position

                // If any pages are visible, update the current page based on the top-most one
                if (visible.length > 0) {
                    const index = Number(visible[0].target.getAttribute('data-index'));
                    setCurrentPage(index + 1); // Pages are 1-based
                }
            },
            { root: viewerRef.current, // Use the viewer container as the scroll root
                threshold: 0.6 } // At least 60% of a page must be visible to count
        );

        // Observe each page element
        pageRefs.current.forEach(ref => ref && observer.observe(ref));

        // Clean up the observer on unmount or when numPages changes
        return () => observer.disconnect();
    }, [numPages]);

    // Automatically focus the viewer container on mount
    useEffect(() => viewerRef.current?.focus(), []);

    return { viewerRef, pageRefs, numPages, setNumPages, currentPage };
}
